import {
  Module, VuexModule, getModule, Mutation, Action,
} from '@compassdigital/vuex-module-decorators';

import {
  AuthApi, ConfigurationParameters, Auth, PartnersApi, PartnerOnboardingStatusEnum,
} from '@compassdigital/foodworks.ts.sdk';
import conf from '@/conf';
import store from '@/store';
import { AccessControl } from 'accesscontrol';
import UserModule from '@/store/modules/user';

function shouldFetchPartnerByOwner(role: string | null | undefined): boolean {
  return role === 'partner_owner' || role === 'unit_manager';
}

export interface IAppState {
  accessToken: string | null;
  authenticated: boolean;
  basePath: string;
  accesscontrol: AccessControl;
  requiresOnboarding: boolean;
  // currentUser: User;
  // currentRole: string;
}

function pagePermissions(ac: AccessControl): void {
  ac.grant('unit_manager')
    .readAny('page-units', ['*'])
    .readAny('page-units-restaurant', ['*']);
  ac.grant('partner_owner')
    .readAny('page-onboarding')
    .readAny('page-units')
    .readAny('page-partners', ['*'])
    .readAny('page-statistics', ['document-statistics', 'scheduler-statistics']);
  ac.grant('fw_manager')
    .readAny('page-users')
    .readAny('page-partners')
    .readAny('page-statistics')
    .readAny('page-units');
  ac.grant('fw_admin')
    .readAny('page-users')
    .readAny('page-partners')
    .readAny('page-configuration')
    .readAny('page-statistics')
    .readAny('page-units');
}

@Module({
  dynamic: true,
  name: 'app',
  namespaced: true,
  store,
})
class AppModule extends VuexModule implements IAppState {
  public accessToken: string | null = localStorage.getItem('ACCESS_TOKEN');

  public authenticated: boolean = false;

  public env: string = conf.env;

  public basePath: string = conf.basePath;

  public accesscontrol: AccessControl = new AccessControl();

  public requiresOnboarding: boolean = false;

  /**
   * Sets the access token otherwise removes access token
   * @param token jwt access token or null
   */
  @Mutation
  private SET_ACCESS_TOKEN(token?: string) {
    if (token) {
      localStorage.setItem('ACCESS_TOKEN', token);
      this.accessToken = token;
    } else {
      this.accessToken = null;
      localStorage.removeItem('ACCESS_TOKEN');
    }
  }

  @Mutation
  public SET_AUTHENTICATED(bool: boolean) {
    this.authenticated = bool;
  }

  @Mutation
  public SET_ONBOARDING(bool: boolean) {
    this.requiresOnboarding = bool;
  }

  @Mutation
  public SET_ACL(data: Auth) {
    if (data.grants) {
      this.accesscontrol.setGrants({ [data.role]: data.grants });
      pagePermissions(this.accesscontrol);
    }
  }

  get apiConfig(): ConfigurationParameters {
    return {
      basePath: this.basePath,
      accessToken: this.accessToken as string,
    };
  }

  @Mutation
  public CLEAR_STATE() {
    this.accesscontrol = new AccessControl();
  }

  public get isLoggedIn() {
    return !!this.authenticated;
  }

  @Action
  public async authenticate(configuration?: ConfigurationParameters): Promise<void> {
    const authApi = configuration ? new AuthApi({ ...this.apiConfig, ...configuration })
      : new AuthApi(this.apiConfig);
    const res = await authApi.getAuth();
    try {
      this.SET_ACCESS_TOKEN(res.data.token);
      this.SET_AUTHENTICATED(true);
      this.SET_ACL(res.data);
      UserModule.SET_CURRENT_USER(res.data.user);
      UserModule.SET_CURRENT_ROLE(res.data.role);

      if (shouldFetchPartnerByOwner(res.data.role)) {
        await this.fetchPartnerInfo(res.data.user.id as string);
      }
    } catch (err) {
      console.log(err);
    }
  }

  @Action
  public async fetchPartnerInfo(userId: string) {
    if (!shouldFetchPartnerByOwner(UserModule.currentRole)) {
      return;
    }
    const partnerApi = new PartnersApi(this.apiConfig);
    const { data: partner } = await partnerApi.getPartners(userId);
    if (partner.length > 0) {
      UserModule.SET_CURRENT_PARTNER(partner[0]);
      const requiresOnboarding = UserModule.currentPartner?.onboarding?.status
        === PartnerOnboardingStatusEnum.AwaitPartner;
      this.SET_ONBOARDING(requiresOnboarding);
    }
  }

  @Action
  public logout() {
    UserModule.clear();
    this.SET_ONBOARDING(false);
    this.SET_AUTHENTICATED(false);
    this.SET_ACCESS_TOKEN();
    this.CLEAR_STATE();
  }
}

export default getModule(AppModule);
